import { Component, EventEmitter, Input, Output, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { AddCodeFormData } from '../../../models/code-management.model';
import { CodeInformationComponent } from '../code-information/code-information.component';
import { ClinicalQuestionnaireFormsComponent } from '../clinical-questionnaire-forms/clinical-questionnaire-forms.component';
import { ModifiersComponent } from '../modifiers/modifiers.component';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { DateService } from '@core/services/date.service';
import { SaveHcpcsRequestDTO, SaveHcpcsResult } from '@core/models';

@Component({
  selector: 'app-add-code',
  templateUrl: './add-code.component.html',
  styleUrls: ['./add-code.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    DialogModule,
    CodeInformationComponent,
    ClinicalQuestionnaireFormsComponent,
    ModifiersComponent
  ]
})
export class AddCodeComponent implements AfterViewInit {
  @Input() editingCode: any = null; 
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<AddCodeFormData>();
  
  @ViewChild(CodeInformationComponent) codeInfoComponent!: CodeInformationComponent;
  @ViewChild(ClinicalQuestionnaireFormsComponent) clinicalFormsComponent!: ClinicalQuestionnaireFormsComponent;
  @ViewChild(ModifiersComponent) modifiersComponent!: ModifiersComponent;

  showUnsavedChangesModal = false;
  isSaving = false;
  showClinicalForms = false;
  clinicalFormsData: any[] | undefined = undefined;
  showModifiers = false;
  modifiersData: any[] | undefined = undefined;
  tenantId: string = '';
  cmsEffectiveStartDate: Date | null = null;
  cmsEffectiveEndDate: Date | null = null;
  planEffectiveStartDate: Date | null = null;
  planEffectiveEndDate: Date | null = null;  
  isExistingCode = false;
  hasFinishedInitialLoad = false;
  hasModifiersLoaded = false;
  hasClinicalFormsLoaded = false;

  constructor(
    private messageService: MessageService,
    private codeManagementApiService: CodeManagementApiService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dateService: DateService
  ) {}

  ngAfterViewInit(): void {
    if (this.codeInfoComponent?.isValid) {
      this.showClinicalForms = true;
      this.cdr.detectChanges();
    }
  }

  onCodeInfoChange(data: any): void {
    if (data.isExistingCode !== undefined) {
      const wasExistingCode = this.isExistingCode;
      this.isExistingCode = data.isExistingCode;
      
      if (this.isExistingCode && !wasExistingCode) {
        this.hasFinishedInitialLoad = false;
        this.hasModifiersLoaded = false;
        this.hasClinicalFormsLoaded = false;
      }
    }
    
    if (data.tenantId) {
      this.tenantId = data.tenantId;
    }
    
    if ('cmsEffectiveStartDate' in data) {
      this.cmsEffectiveStartDate = data.cmsEffectiveStartDate;
    }
    if ('cmsEffectiveEndDate' in data) {
      this.cmsEffectiveEndDate = data.cmsEffectiveEndDate;
    }
    if ('planEffectiveStartDate' in data) {
      this.planEffectiveStartDate = data.planEffectiveStartDate;
    }
    if ('planEffectiveEndDate' in data) {
      this.planEffectiveEndDate = data.planEffectiveEndDate;
    }
    
    if (!this.showClinicalForms && this.codeInfoComponent?.isValid) {
      this.showClinicalForms = true;
      this.showModifiers = true;
      if (this.clinicalFormsData === undefined && !data.isExistingCode) {
        this.clinicalFormsData = [];
      }
      if (this.modifiersData === undefined && !data.isExistingCode) {
        this.modifiersData = [];
      }
    }
  }

  onCodeSelected(isSelected: boolean): void {
    this.showClinicalForms = isSelected;
    this.showModifiers = isSelected;
    if (!isSelected) {
      this.clinicalFormsData = undefined;
      this.modifiersData = undefined;
      this.cmsEffectiveStartDate = null;
      this.cmsEffectiveEndDate = null;
      this.planEffectiveStartDate = null;
      this.planEffectiveEndDate = null;
    }
  }

  onClinicalFormsLoaded(forms: any[]): void {
    this.clinicalFormsData = forms;
  }

  onModifiersLoaded(modifiers: any[]): void {
    this.modifiersData = modifiers;
  }
  
  onModifiersInitialLoadComplete(): void {
    this.hasModifiersLoaded = true;
    this.checkIfAllInitialLoadsComplete();
  }
  
  onClinicalFormsInitialLoadComplete(): void {
    this.hasClinicalFormsLoaded = true;
    this.checkIfAllInitialLoadsComplete();
  }
  
  private checkIfAllInitialLoadsComplete(): void {
    if (this.isExistingCode && !this.hasFinishedInitialLoad && 
        this.hasModifiersLoaded && this.hasClinicalFormsLoaded) {
      
      this.markAllFormsAsPristine();
      this.hasFinishedInitialLoad = true;
    }
  }

  private markAllFormsAsPristine(): void {
    this.codeInfoComponent?.codeInfoForm?.markAsPristine();
    this.modifiersComponent?.modifiersFormGroup?.markAsPristine();
    this.clinicalFormsComponent?.formsFormGroup?.markAsPristine();
  }

  onCancel(): void {
    if (this.hasAnyFormChanges()) {
      this.showUnsavedChangesModal = true;
    } else {
      this.cancel.emit();
    }
  }

  private hasAnyFormChanges(): boolean {
    const codeInfoDirty = this.codeInfoComponent?.codeInfoForm?.dirty ?? false;
    const modifiersDirty = this.modifiersComponent?.modifiersFormGroup?.dirty ?? false;
    const formsDirty = this.clinicalFormsComponent?.formsFormGroup?.dirty ?? false;
    return codeInfoDirty || modifiersDirty || formsDirty;
  }

  confirmDiscard(): void {
    this.showUnsavedChangesModal = false;
    this.cancel.emit();
  }

  backToForm(): void {
    this.showUnsavedChangesModal = false;
  }

  onSaveClick(): void {
    if (!this.isFormValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields'
      });
      return;
    }

    const formData = this.codeInfoComponent.getFormValue();
    this.onSave(formData);
  }

  onSave(formData: AddCodeFormData): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    const searchCodeValue = this.codeInfoComponent.codeInfoForm.get('searchCode')?.value || '';
    const codeValue = this.codeInfoComponent.codeInfoForm.get('code')?.value || '';
    const currentSearchCode = codeValue || searchCodeValue || formData.searchCode;
    
    const selectedCodeMatches = this.codeInfoComponent.selectedCodeResult?.code === currentSearchCode;
    const hcpcsId = (selectedCodeMatches && this.codeInfoComponent.isExistingCode) 
      ? this.codeInfoComponent.selectedCodeResult?.hcpcsId || null 
      : null;
    const hcpcsCode = currentSearchCode;

    const formatDateForAPI = (date: any): string | null => {
      if (!date) return null;
      return this.dateService.transformPOSTDate(date);
    };

    const clinicalForms = this.clinicalFormsComponent?.getFormsData() || [];
    const modifiers = this.modifiersComponent?.getModifiersData() || [];
    
    const request: SaveHcpcsRequestDTO = {
      hcpcsId: hcpcsId,
      hcpcsCode: hcpcsCode,
      shortDescription: formData.shortDescription,
      cmsEffectiveStartDate: formatDateForAPI(formData.cmsEffectiveStartDate) ?? this.dateService.transformPOSTDate(new Date())!,
      cmsEffectiveEndDate: formatDateForAPI(formData.cmsEffectiveEndDate) ?? null,
      productId: formData.program,
      tenantId: formData.healthPlan,
      overrideTypeId: formData.planType,
      planEffectiveStartDate: formatDateForAPI(formData.planEffectiveStartDate) ?? this.dateService.transformPOSTDate(new Date())!,
      planEffectiveEndDate: formatDateForAPI(formData.planEffectiveEndDate) ?? null,
      dmeposCategoryId: formData.dmeposCategory || null,
      hcpcsClinicalForms: clinicalForms,
      hcpcsModifiers: modifiers
    };
    this.codeManagementApiService.saveHcpcsWithAssociation(request).subscribe({
      next: (response) => {
        this.isSaving = false;
        
        this.markAllFormsAsPristine();
        
        this.modifiersData = this.modifiersComponent?.getModifiersData();
        this.clinicalFormsData = this.clinicalFormsComponent?.getFormsData();

        if (response.result === SaveHcpcsResult.Created) {
          this.messageService.add({
            severity: 'success',
            summary: 'Code Added',
            detail: 'Code was successfully added'
          });
          this.emitSaveAfterDelay();
        } else if (response.result === SaveHcpcsResult.Updated) {
          this.messageService.add({
            severity: 'success',
            summary: 'Code Saved',
            detail: 'Code was successfully saved'
          });
          this.emitSaveAfterDelay();
        } else if (response.result === SaveHcpcsResult.DuplicateCode) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Unable to add code',
            detail: 'Code already exists'
          });
        } else if (response.result === SaveHcpcsResult.Failed) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Unable to Save',
            detail: response.errorMessage || 'Failed to save code.'
          });
        }
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again or contact Portal support.'
        });
      }
    });
  }

  private emitSaveAfterDelay(): void {
    setTimeout(() => {
      this.save.emit();
    }, 2000);
  }

  get isFormValid(): boolean {
    const codeInfoValid = this.codeInfoComponent?.isValid ?? false;
    if (!codeInfoValid) return false;
    
    const hasClinicalForms = (this.clinicalFormsComponent?.formsList?.length ?? 0) > 0;
    if (!hasClinicalForms) return false;    
    
    const hasEditingForms = this.clinicalFormsComponent?.formsList.controls.some(
      control => control.get('isEditing')?.value === true
    ) ?? false;
    if (hasEditingForms) return false;
    
    const hasEditingModifiers = this.modifiersComponent?.modifiersList.controls.some(
      control => control.get('isEditing')?.value === true
    ) ?? false;
    if (hasEditingModifiers) return false;
    
    if (this.isExistingCode) {
      if (!this.hasFinishedInitialLoad) return false;
      
      const anyFormDirty = 
        (this.codeInfoComponent?.codeInfoForm?.dirty ?? false) ||
        (this.modifiersComponent?.modifiersFormGroup?.dirty ?? false) ||
        (this.clinicalFormsComponent?.formsFormGroup?.dirty ?? false);
      
      const modifiersChanged = (this.modifiersData?.length ?? 0) !== (this.modifiersComponent?.modifiersList?.length ?? 0);
      const formsChanged = (this.clinicalFormsData?.length ?? 0) !== (this.clinicalFormsComponent?.formsList?.length ?? 0);
      
      return anyFormDirty || modifiersChanged || formsChanged;
    }
    
    return true;
  }
}
